import { Spinner, Center, HStack, Box, Flex } from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  useLunaStore,
  useActiveSessionId,
  useLunaIsConnecting,
} from "state/luna";
import { LunaChatView } from "components/Luna/LunaChatView";
import { LunaHeader } from "components/Luna/LunaHeader";
import { ArtifactViewer } from "components/Luna/ArtifactViewer";

const LunaSessionPage = () => {
  const { sessionId: urlSessionId, "*": wildcard } = useParams<{
    sessionId?: string;
    "*"?: string;
  }>();
  // Parse artifact ID from wildcard path (e.g., "artifact/abc123" -> "abc123")
  const artifactMatch = wildcard?.match(/^artifact\/(.+)$/);
  const urlArtifactId = artifactMatch ? artifactMatch[1] : undefined;
  const location = useLocation();
  const navigate = useNavigate();

  const activeSessionId = useActiveSessionId();
  const setActiveSession = useLunaStore((state) => state.setActiveSession);
  const connectToSession = useLunaStore((state) => state.connectToSession);
  const disconnectFromSession = useLunaStore(
    (state) => state.disconnectFromSession,
  );
  const sendMessage = useLunaStore((state) => state.sendMessage);
  const refreshHistory = useLunaStore((state) => state.refreshHistory);
  const isConnecting = useLunaIsConnecting(
    activeSessionId || urlSessionId || null,
  );

  // Track which session we're connecting to avoid duplicate connections
  const connectingSessionRef = useRef<string | null>(null);
  // Track if we've processed the initial message
  const initialMessageProcessedRef = useRef(false);

  // Track if this is the initial mount
  const isInitialMountRef = useRef(true);

  // Handle session connection based on URL - only on initial mount
  useEffect(() => {
    if (!isInitialMountRef.current) return;
    isInitialMountRef.current = false;

    if (urlSessionId) {
      if (connectingSessionRef.current !== urlSessionId) {
        const isPlaybook =
          (location.state as { isPlaybook?: boolean })?.isPlaybook ?? false;

        connectingSessionRef.current = urlSessionId;
        setActiveSession(urlSessionId);
        connectToSession(urlSessionId, { isPlaybook })
          .then(() => {
            connectingSessionRef.current = null;
          })
          .catch(() => {
            connectingSessionRef.current = null;
          });
      }
    }
  }, [urlSessionId]);

  // Handle browser back/forward navigation (popstate)
  useEffect(() => {
    const handlePopState = () => {
      const artifactMatch = window.location.pathname.match(
        /^\/luna\/requests\/([^/]+)\/artifact\/([^/]+)$/,
      );
      const sessionMatch = window.location.pathname.match(
        /^\/luna\/requests\/([^/]+)$/,
      );

      const newSessionId = artifactMatch
        ? artifactMatch[1]
        : sessionMatch
          ? sessionMatch[1]
          : null;

      if (newSessionId && newSessionId !== activeSessionId) {
        if (activeSessionId) {
          disconnectFromSession(activeSessionId);
        }

        setActiveSession(newSessionId);
        connectToSession(newSessionId);
      } else if (!newSessionId && activeSessionId) {
        disconnectFromSession(activeSessionId);
        setActiveSession(null);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [
    activeSessionId,
    disconnectFromSession,
    setActiveSession,
    connectToSession,
  ]);

  // Handle initial message from navigation state (separate effect)
  // Wait until connection is complete (isConnecting is false) before sending
  useEffect(() => {
    if (
      !urlSessionId ||
      !activeSessionId ||
      activeSessionId !== urlSessionId ||
      isConnecting
    ) {
      return;
    }

    const initialMessage = (location.state as { initialMessage?: string })
      ?.initialMessage;

    if (initialMessage && !initialMessageProcessedRef.current) {
      initialMessageProcessedRef.current = true;
      sendMessage(urlSessionId, initialMessage);
      navigate(location.pathname, { replace: true, state: {} });

      // Fallback: reload history after a delay to catch any missed websocket events
      const timeoutId = setTimeout(() => {
        refreshHistory(urlSessionId);
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [urlSessionId, activeSessionId, isConnecting, location.state]);

  // Reset initial message flag when URL changes
  useEffect(() => {
    initialMessageProcessedRef.current = false;
  }, [urlSessionId]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      const currentState = useLunaStore.getState();
      const sessionId =
        connectingSessionRef.current || currentState.activeSessionId;
      if (sessionId) {
        currentState.disconnectFromSession(sessionId);
      }
      currentState.setActiveSession(null);
    };
  }, []);

  return (
    <Flex direction="column" h="full" w="full" overflow="hidden">
      <LunaHeader sessionId={activeSessionId} />

      <HStack flex={1} spacing={0} align="stretch" minH={0}>
        <Box flex={1} h="full" minW={0}>
          {activeSessionId && !isConnecting ? (
            <LunaChatView sessionId={activeSessionId} />
          ) : (
            <Center h="full" w="full">
              <Spinner size="lg" color="p.600" />
            </Center>
          )}
        </Box>

        {/* Artifact viewer - spans full height as sibling */}
        <ArtifactViewer />
      </HStack>
    </Flex>
  );
};

export default LunaSessionPage;
